import React, {CSSProperties, ReactNode, RefObject, useLayoutEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {getWidthWithMarginOfElement} from '../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {usePrevious} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {Renderer, RendererOf} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useContentSize} from '../../../../libs/shared/ui/src/hooks/sizeHooks';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';

/*
 * Props.
 */

export interface OverflowWithCountProps<T> {
  elements: ReadonlyArray<T>;
  /** Function to build each element's key (keys must be immutable and unique amongst siblings). */
  buildElementKey?: ElementKeyBuilderFunction<T>;
  renderElement: ElementRendererFunction<T>;
  renderOverflowIndicator: RendererOf<number>;
  renderSeparator?: () => ReactNode;
  /** If defined, if there is no space to render any items, render this instead. */
  renderIfTooSmall?: (count: number) => ReactNode;
  shouldRenderPartial?: boolean;
  overflowContainerWidth?: number;
  className?: string;
  renderTooltip?: Renderer;
  shouldUseMaximumWidth?: boolean;
  /** When shouldRenderPartial = true, set the minimum width in order to have the item displayable. This include should include the width of separator. */
  minVisibleItemWidth?: number;
  listDivStyles?: CSSProperties;
}

/*
 * Types.
 */

type ElementKeyBuilderFunction<T> = (element: T) => number | string;

type ElementRendererFunction<T> = (
  element: T,
  index: number,
  hideStartIndex: number | undefined,
) => React.ReactNode;

interface ElementSizeDetail {
  index: number;
  width: number;
}

/*
 * Style.
 */

const StyledOverflowWithCountContainerDiv = styled.div`
  display: flex;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
`;
const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  display: flex;
  overflow: hidden;
  width: 100%;
`;

const StyledVisibleElementsDiv = styled.div`
  display: flex;
  flex-flow: row;
  white-space: nowrap;
  overflow: hidden;
`;

const hiddenStyles = css`
  visibility: hidden;
  position: absolute;
  pointer-events: none;
`;

const StyledHiddenDiv = styled.div`
  ${hiddenStyles}
`;

interface StyledElementWrapperSpanProps {
  $isHidden: boolean;
  $isEllipsisElement: boolean;
  $maxWidth: number;
}
const StyledOverflowWithCountElementWrapperDiv = styled.div<StyledElementWrapperSpanProps>`
  white-space: nowrap;
  ${(p) => css`
    max-width: ${p.$maxWidth}px;
  `}
  ${(p) =>
    p.$isEllipsisElement &&
    css`
      ${ellipsis()}
    `}
    ${(p) => p.$isHidden && hiddenStyles};
`;

/*
 * Component.
 */

export const OverflowWithCount = <T,>(props: OverflowWithCountProps<T>) => {
  const {
    elements,
    shouldRenderPartial,
    overflowContainerWidth,
    buildElementKey,
    renderElement,
    renderOverflowIndicator,
    renderSeparator,
    shouldUseMaximumWidth = false,
    minVisibleItemWidth = 0,
    listDivStyles,
    renderIfTooSmall,
    className,
  } = props;

  const rowRef = useRef<HTMLDivElement>(null);
  const {width: rowWidth = 0} = useContentSize(rowRef);
  const visibleContainerRef = useRef<HTMLDivElement>(null);
  const hiddenIndicatorRef = useRef<HTMLDivElement>(null);
  const {width: hiddenIndicatorWidth = 0} = useContentSize(hiddenIndicatorRef);
  const [hideStartIndex, setHideStartIndex] = useState<number | undefined>(undefined);
  const previousElementsLength = usePrevious(elements.length);

  const maximumRowWidth =
    overflowContainerWidth ?? (shouldUseMaximumWidth ? computeMaximumWidth(rowRef.current) : undefined);
  const previousMaximumRowWidth = usePrevious(maximumRowWidth);
  const previousMinVisibleItemWidth = usePrevious(minVisibleItemWidth);

  const hasRenderIfTooSmall = renderIfTooSmall !== undefined;

  useLayoutEffect(() => {
    // If the overflow container doesn't change, we don't need to recompute the hideStartIndex.
    // Also watch elements, if new ones are added or removed we need to re-compute.
    if (
      maximumRowWidth &&
      hiddenIndicatorWidth !== 0 &&
      maximumRowWidth === previousMaximumRowWidth &&
      elements.length === previousElementsLength &&
      minVisibleItemWidth === previousMinVisibleItemWidth
    ) {
      return;
    }

    if (!visibleContainerRef.current) {
      return;
    }

    // First calculate the widths of the elements.
    const visibleRender = visibleContainerRef.current;
    const visibleElements = Array.from(visibleRender.children);
    const elementSizeDetails: Array<ElementSizeDetail> = visibleElements.map((visibleElement, index) => ({
      index,
      width: getWidthWithMarginOfElement(visibleElement),
    }));

    // Then figure out which element can fit in the space we have available.
    // We compute the hideStartIndex, which is the index in the array of visible elements at which we should start
    // hiding the element. If currentHideStartIndex never gets set, we don't need to hide any elements.
    const computedHideStartIndex = computeHideStartIndex(
      rowWidth,
      hiddenIndicatorWidth,
      elementSizeDetails,
      shouldRenderPartial,
      maximumRowWidth,
      minVisibleItemWidth,
      hasRenderIfTooSmall,
    );
    setHideStartIndex(computedHideStartIndex);
  }, [
    elements.length,
    rowWidth,
    hiddenIndicatorWidth,
    shouldRenderPartial,
    setHideStartIndex,
    maximumRowWidth,
    minVisibleItemWidth,
    hasRenderIfTooSmall,
  ]);

  return (
    <StyledTooltipCoordinator
      render={() => {
        if (!hideStartIndex || hideStartIndex > elements.length || !props.renderTooltip) {
          return null;
        }

        return props.renderTooltip();
      }}
    >
      <StyledOverflowWithCountContainerDiv ref={rowRef} className={className}>
        {hideStartIndex === 0 && hasRenderIfTooSmall && renderIfTooSmall(elements.length)}
        <StyledVisibleElementsDiv>
          {renderVisibleElements(
            buildElementKey,
            renderElement,
            elements,
            hideStartIndex,
            shouldRenderPartial,
            (maximumRowWidth ?? rowWidth ?? 0) - hiddenIndicatorWidth,
            renderSeparator,
            listDivStyles,
          )}
        </StyledVisibleElementsDiv>
        {/* Render all elements in a hidden div to compute their size. */}
        <StyledHiddenDiv ref={visibleContainerRef}>
          {renderVisibleElements(
            buildElementKey,
            renderElement,
            elements,
            0,
            false,
            (maximumRowWidth ?? rowWidth ?? 0) - hiddenIndicatorWidth,
            renderSeparator,
            listDivStyles,
          )}
        </StyledHiddenDiv>
        {maybeRenderHiddenIndicator(
          renderOverflowIndicator,
          hiddenIndicatorRef,
          elements.length,
          hideStartIndex,
        )}
      </StyledOverflowWithCountContainerDiv>
    </StyledTooltipCoordinator>
  );
};

/*
 * Helpers.
 */

function renderVisibleElements<T>(
  buildElementKey: ElementKeyBuilderFunction<T> | undefined,
  renderElement: ElementRendererFunction<T>,
  elements: ReadonlyArray<T>,
  hideStartIndex: number | undefined,
  shouldRenderPartial: boolean | undefined,
  maxWidth: number,
  renderSeparator: (() => ReactNode) | undefined,
  listDivStyles: CSSProperties | undefined,
) {
  return (
    <>
      {elements.map((element, index) => (
        <StyledOverflowWithCountElementWrapperDiv
          // eslint-disable-next-line react/no-array-index-key
          key={buildElementKey?.(element) ?? `wrapped-element-${index}`}
          $isHidden={hideStartIndex !== undefined ? index >= hideStartIndex : false}
          $maxWidth={maxWidth}
          $isEllipsisElement={
            Boolean(shouldRenderPartial && elements.length === 1) ||
            Boolean(shouldRenderPartial && hideStartIndex !== undefined && hideStartIndex - 1 === index)
          }
          style={listDivStyles}
        >
          {index !== 0 && renderSeparator?.()}
          {renderElement(element, index, hideStartIndex)}
        </StyledOverflowWithCountElementWrapperDiv>
      ))}
    </>
  );
}

function maybeRenderHiddenIndicator(
  renderOverflowIndicator: RendererOf<number>,
  ref: RefObject<HTMLDivElement>,
  numberOfElements: number,
  hideStartIndex: number | undefined,
) {
  if (!hideStartIndex || hideStartIndex > numberOfElements) {
    return null;
  }

  const numberOfHiddenElements = numberOfElements - hideStartIndex;
  if (numberOfHiddenElements === 0) {
    return null;
  }
  return <span ref={ref}>{renderOverflowIndicator(numberOfHiddenElements)}</span>;
}

function computeHideStartIndex(
  rowWidth: number,
  hiddenIndicatorWidth: number,
  sizeDetails: Array<ElementSizeDetail>,
  shouldRenderPartial: boolean | undefined,
  maxContainerWidth: number | undefined,
  minVisibleItemWidth: number,
  hasRenderIfTooSmall: boolean,
) {
  const maxAvailableWidth = maxContainerWidth ?? rowWidth;
  const availableWidth = maxAvailableWidth - hiddenIndicatorWidth;

  const reducedResult = sizeDetails.reduce<{
    hideStartIndex: number | undefined;
    currentWidth: number;
  }>(
    (acc, tagSizeDetail) => {
      if (acc.hideStartIndex !== undefined) {
        return acc;
      }

      const leftX = acc.currentWidth;
      const leftXBoundary = leftX + minVisibleItemWidth;
      const rightX = acc.currentWidth + tagSizeDetail.width;

      let hideStartIndex: number | undefined;

      // If left side of the element is already outside the row width, we should hide it.
      if (leftXBoundary > availableWidth) {
        hideStartIndex = tagSizeDetail.index;
      } else if (rightX > availableWidth) {
        if (!shouldRenderPartial) {
          hideStartIndex = tagSizeDetail.index;
        }
      }

      // If we're at the boundary, also check if we can fit inside the row width without the indicator width.
      // Applies if we're already showing the indicator and the user resizes the pane such that we don't
      // need to show the indicator anymore.
      if (hideStartIndex && hideStartIndex === sizeDetails.length - 1 && rightX < maxAvailableWidth) {
        hideStartIndex = undefined;
      }

      return {currentWidth: rightX, hideStartIndex};
    },
    {hideStartIndex: undefined, currentWidth: 0},
  );

  return Math.max(reducedResult.hideStartIndex ?? sizeDetails.length, hasRenderIfTooSmall ? 0 : 1);
}

function computeMaximumWidth(element: HTMLElement | null): number {
  if (!element) {
    return 0;
  }

  const fakeDiv = document.createElement('div');
  fakeDiv.style.width = '10000px';
  fakeDiv.style.visibility = 'hidden';
  element.appendChild(fakeDiv);
  const width = element.clientWidth;
  fakeDiv.remove();

  return width;
}
